/**
 * Pure assembly for Finances → Forecast cash projection inputs and series.
 *
 * Extracts the no-behavior input-building that lived in
 * `useCashFlowProjectionData` and `ForecastView` so the finance API can run
 * the same path via `functions/src/pageForecast/` copies. Callers still own
 * Firestore listeners / React state — this module only shapes values.
 */

import type {
  ChangeOrder,
  EnhancedDeal,
  Milestone,
  ProjectTask,
  QBInvoice,
  SalesOrder,
  TaskGroup,
  TimberProject,
} from '../types';
import type { ObligationSlice } from '../types/finances';
import { awardedQuotesMissingFromBills } from './awardedQuoteForecastGaps';
import {
  buildCashFlowProjection,
  type CashFlowProjection,
  type CashFlowProjectionInputs,
  type CashFlowProjectionItem,
  type JobCostProjectionSlice,
} from './cashFlowProjection';
import type { ForecastBucket } from './financesForecast';
import type { PayrollSchedule } from './leaveAccrual';
import {
  rollDailyWalkToWeeks,
  toDayKey,
  walkForecastDailyBalances,
  type ForecastDailyBalancePoint,
  type ForecastWeeklyBalancePoint,
} from './forecastDailyChart';
import { mirrorAllOnboardingTasks } from './onboardingTaskBridge';
import {
  pickTimberProjectForDealSchedule,
  taskEffectiveEndDate,
  type PaymentLagSettings,
  type TaskScheduleLike,
} from './paymentDueFlags';
import { getProjectDisplayName } from './projectName';
import {
  buildReadyToBillQueue,
  type BillingReadyItem,
  type ReadyToBillRow,
} from './readyToBill';
import type { SupplierBill } from './supplierBills';

export type ForecastScheduleTask = ProjectTask & { id?: string };

/** Mirror onboarding checklist rows onto the schedule task list (page behavior). */
export function buildForecastScheduleTasks(
  projectTasks: ProjectTask[],
  projectOnboardings: Parameters<typeof mirrorAllOnboardingTasks>[0],
  taskGroups: TaskGroup[],
): ForecastScheduleTask[] {
  return [...projectTasks, ...mirrorAllOnboardingTasks(projectOnboardings, taskGroups)];
}

export function buildMoneyInRows(opts: {
  salesOrders: Array<SalesOrder & { id: string }>;
  qbInvoices: Array<QBInvoice & { id: string }>;
  deals: EnhancedDeal[];
  timberProjects: TimberProject[];
  milestones: Milestone[];
  changeOrders: ChangeOrder[];
  trackedItems: BillingReadyItem[];
  projectTasks: ForecastScheduleTask[];
  taskGroups: TaskGroup[];
  paymentLagSettings?: PaymentLagSettings | null;
}): ReadyToBillRow[] {
  const baseSalesOrders = opts.salesOrders.filter((so) => !so.isChangeOrder);
  const coSalesOrders = opts.salesOrders.filter((so) => so.isChangeOrder);
  return buildReadyToBillQueue({
    salesOrders: baseSalesOrders,
    coSalesOrders,
    qbInvoices: opts.qbInvoices,
    deals: opts.deals,
    timberProjects: opts.timberProjects,
    milestones: opts.milestones,
    changeOrders: opts.changeOrders,
    trackedItems: opts.trackedItems,
    projectTasks: opts.projectTasks,
    taskGroups: opts.taskGroups,
    paymentLagSettings: opts.paymentLagSettings ?? undefined,
    forProjectSummary: true,
  });
}

export function buildSupplierTaskEndDates(
  scheduleTasks: Array<Partial<TaskScheduleLike> & { id?: string }>,
): Map<string, Date> {
  const m = new Map<string, Date>();
  for (const t of scheduleTasks) {
    if (!t.id) continue;
    const end = taskEffectiveEndDate({
      projectId: t.projectId || '',
      endDate: t.endDate,
      actualEndDate: t.actualEndDate,
      status: t.status,
      id: t.id,
      title: t.title,
      groupId: t.groupId,
      parentTaskId: t.parentTaskId,
    });
    if (end) m.set(t.id, end);
  }
  return m;
}

export function buildTaskNameById(
  scheduleTasks: Array<{ id?: string; title?: string }>,
): Map<string, string> {
  const m = new Map<string, string>();
  for (const t of scheduleTasks) {
    if (t.id && t.title) m.set(t.id, t.title);
  }
  return m;
}

export function buildForecastProjectMaps(opts: {
  enhancedDeals: EnhancedDeal[];
  timberProjects: TimberProject[];
  scheduleTasks: TaskScheduleLike[];
  taskGroups: TaskGroup[];
}): {
  projectNameById: Map<string, string>;
  projectIdByDealId: Map<string, string>;
  taskProjectIdById: Map<string, string>;
} {
  const projectNameById = new Map<string, string>();
  const projectIdByDealId = new Map<string, string>();
  const dealsById = new Map(
    opts.enhancedDeals.filter((d) => d.id).map((d) => [d.id as string, d] as const),
  );
  const dealIds = Array.from(
    new Set(opts.timberProjects.map((p) => p.dealId).filter((id): id is string => Boolean(id))),
  );
  for (const dealId of dealIds) {
    const project = pickTimberProjectForDealSchedule(
      opts.timberProjects,
      dealId,
      opts.scheduleTasks,
      opts.taskGroups,
    );
    if (!project?.id) continue;
    projectIdByDealId.set(dealId, project.id);
    const deal = dealsById.get(dealId);
    const name = (deal ? getProjectDisplayName(deal) : '') || project.name;
    if (name) projectNameById.set(project.id, name);
  }
  for (const project of opts.timberProjects) {
    if (!project.id || projectNameById.has(project.id)) continue;
    const deal = project.dealId ? dealsById.get(project.dealId) : undefined;
    const name = (deal ? getProjectDisplayName(deal) : '') || project.name;
    if (name) projectNameById.set(project.id, name);
  }
  const taskProjectIdById = new Map<string, string>();
  for (const t of opts.scheduleTasks) {
    if (t.id && t.projectId) taskProjectIdById.set(t.id, t.projectId);
  }
  return { projectNameById, projectIdByDealId, taskProjectIdById };
}

export function buildCompletedProjectIds(timberProjects: TimberProject[]): Set<string> {
  return new Set(
    timberProjects
      .filter((p) => p.status === 'completed' && p.id)
      .map((p) => p.id as string),
  );
}

export function buildDealIdByProjectId(timberProjects: TimberProject[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const project of timberProjects) {
    if (project.id && project.dealId) map.set(project.id, project.dealId);
  }
  return map;
}

export function buildAwardedQuoteApFromPage(opts: {
  rfqProjects: Parameters<typeof awardedQuotesMissingFromBills>[0]['rfqProjects'];
  rfqSubmissions: Parameters<typeof awardedQuotesMissingFromBills>[0]['rfqSubmissions'];
  supplierBills: SupplierBill[];
}): ReturnType<typeof awardedQuotesMissingFromBills> {
  return awardedQuotesMissingFromBills(opts);
}

/** Assemble the exact object `buildCashFlowProjection` expects (page shape). */
export function assembleCashFlowProjectionInputs(
  opts: CashFlowProjectionInputs,
): CashFlowProjectionInputs {
  return {
    startingBalance: opts.startingBalance,
    moneyInRows: opts.moneyInRows,
    supplierBills: opts.supplierBills,
    supplierTaskEndDates: opts.supplierTaskEndDates,
    taskNameById: opts.taskNameById,
    projectNameById: opts.projectNameById,
    projectIdByDealId: opts.projectIdByDealId,
    taskProjectIdById: opts.taskProjectIdById,
    burnBuckets: opts.burnBuckets,
    plannedItems: opts.plannedItems,
    jobCostItems: opts.jobCostItems,
    quotePlaceholderAp: opts.quotePlaceholderAp,
    issuedPurchaseAp: opts.issuedPurchaseAp,
    awardedQuoteAp: opts.awardedQuoteAp,
    omitSupplierBillIds: opts.omitSupplierBillIds,
    contractorInvoiceAp: opts.contractorInvoiceAp,
    opexBudgetMonthlyByGroup: opts.opexBudgetMonthlyByGroup,
    payrollSchedule: opts.payrollSchedule,
    billPayClearing: opts.billPayClearing,
    monthsAhead: opts.monthsAhead,
    now: opts.now,
  };
}

export function buildPageCashFlowProjection(
  opts: CashFlowProjectionInputs,
): CashFlowProjection {
  return buildCashFlowProjection(assembleCashFlowProjectionInputs(opts));
}

export type ForecastPageDailyPoint = {
  date: string;
  projected_cash_eod: number;
  ar_in: number;
  ap_out: number;
  planned_out: number;
  job_costs_out: number;
  burn_out: number;
  ar_items: Array<{
    kind: CashFlowProjectionItem['kind'];
    label: string;
    projectLabel: string;
    amount: number;
    dueDate: string | null;
    estimated: boolean;
    billingItemId?: string;
    dealId?: string;
  }>;
};

export type ForecastPageWeekPoint = {
  week_key: string;
  week_start: string;
  week_end: string;
  projected_cash_eod: number;
  ar_in: number;
  ap_out: number;
  planned_out: number;
  job_costs_out: number;
  burn_out: number;
};

export type ForecastPageMonthPoint = {
  month_key: string;
  projected_cash_eod: number;
  ar_in: number;
  ap_out: number;
  planned_out: number;
  job_costs_out: number;
  burn_out: number;
  items: CashFlowProjectionItem[];
};

export type ForecastPageSeries = {
  daily: ForecastPageDailyPoint[];
  weekly: ForecastPageWeekPoint[];
  monthly: ForecastPageMonthPoint[];
  first_negative_day: string | null;
  permanently_negative_day: string | null;
  month_end_balances: Array<{ month_key: string; ending_balance: number }>;
};

function ymdFromDate(d: Date | null | undefined): string | null {
  if (!d || Number.isNaN(d.getTime())) return null;
  return toDayKey(d);
}

function serializeArItems(items: CashFlowProjectionItem[]): ForecastPageDailyPoint['ar_items'] {
  return items
    .filter((item) => item.kind === 'ar')
    .map((item) => ({
      kind: item.kind,
      label: item.label,
      projectLabel: item.projectLabel,
      amount: item.amount,
      dueDate: ymdFromDate(item.dueDate),
      estimated: item.estimated,
      billingItemId: item.billingItemId,
      dealId: item.dealId,
    }));
}

/** First calendar day whose end-of-day projected cash is negative. */
export function findFirstNegativeDay(
  points: Array<Pick<ForecastDailyBalancePoint, 'dayKey' | 'projectedCash'>>,
): string | null {
  for (const point of points) {
    if (point.projectedCash < 0) return point.dayKey;
  }
  return null;
}

/**
 * First day after which projected cash never returns to non-negative
 * (every remaining day in the walk stays < 0).
 */
export function findPermanentlyNegativeDay(
  points: Array<Pick<ForecastDailyBalancePoint, 'dayKey' | 'projectedCash'>>,
): string | null {
  let candidate: string | null = null;
  for (let i = 0; i < points.length; i += 1) {
    if (points[i].projectedCash >= 0) {
      candidate = null;
      continue;
    }
    if (candidate == null) candidate = points[i].dayKey;
  }
  return candidate;
}

export function dailyPointsFromWalk(
  walk: ForecastDailyBalancePoint[],
): ForecastPageDailyPoint[] {
  return walk.map((point) => ({
    date: point.dayKey,
    projected_cash_eod: point.projectedCash,
    ar_in: point.arIn,
    ap_out: point.apOut,
    planned_out: point.plannedOut,
    job_costs_out: point.jobOut,
    burn_out: point.burn,
    ar_items: serializeArItems(point.items),
  }));
}

export function weeklyPointsFromWalk(
  weeks: ForecastWeeklyBalancePoint[],
): ForecastPageWeekPoint[] {
  return weeks.map((week) => ({
    week_key: week.weekKey,
    week_start: toDayKey(week.weekStart),
    week_end: toDayKey(week.weekEnd),
    projected_cash_eod: week.projectedCash,
    ar_in: week.arIn,
    ap_out: week.apOut,
    planned_out: week.plannedOut,
    job_costs_out: week.jobOut,
    burn_out: week.burn,
  }));
}

export function monthlyPointsFromProjection(
  projection: CashFlowProjection,
): ForecastPageMonthPoint[] {
  return projection.months.map((month) => ({
    month_key: month.monthKey,
    projected_cash_eod: month.endingBalance,
    ar_in: month.arIn,
    ap_out: month.apOut,
    planned_out: month.plannedOut,
    job_costs_out: month.jobOut,
    burn_out: month.burn,
    items: month.items,
  }));
}

/**
 * Day / week / month series for the Forecast page (and API parity).
 * Month-end balances come from the projection months (dollar-identical to the chart).
 */
export function buildForecastPageSeries(opts: {
  projection: CashFlowProjection;
  today: Date;
  payrollSchedule?: PayrollSchedule | null;
}): ForecastPageSeries {
  const walk = walkForecastDailyBalances({
    months: opts.projection.months,
    startingBalance: opts.projection.startingBalance,
    today: opts.today,
    payrollSchedule: opts.payrollSchedule,
  });
  const weeks = rollDailyWalkToWeeks(walk);
  return {
    daily: dailyPointsFromWalk(walk),
    weekly: weeklyPointsFromWalk(weeks),
    monthly: monthlyPointsFromProjection(opts.projection),
    first_negative_day: findFirstNegativeDay(walk),
    permanently_negative_day: findPermanentlyNegativeDay(walk),
    month_end_balances: opts.projection.months.map((m) => ({
      month_key: m.monthKey,
      ending_balance: m.endingBalance,
    })),
  };
}

/** Stable fingerprint for frozen fixture / parity asserts. */
export function forecastPageSeriesFingerprint(series: ForecastPageSeries): {
  month_end_balances: ForecastPageSeries['month_end_balances'];
  first_negative_day: string | null;
  permanently_negative_day: string | null;
  daily_count: number;
  weekly_count: number;
  monthly_count: number;
  daily_last: ForecastPageDailyPoint | null;
  weekly_last: ForecastPageWeekPoint | null;
  line_items_by_month: Array<{
    month_key: string;
    items: Array<{ kind: string; label: string; projectLabel: string; amount: number }>;
  }>;
} {
  return {
    month_end_balances: series.month_end_balances,
    first_negative_day: series.first_negative_day,
    permanently_negative_day: series.permanently_negative_day,
    daily_count: series.daily.length,
    weekly_count: series.weekly.length,
    monthly_count: series.monthly.length,
    daily_last: series.daily.length ? series.daily[series.daily.length - 1] : null,
    weekly_last: series.weekly.length ? series.weekly[series.weekly.length - 1] : null,
    line_items_by_month: series.monthly.map((m) => ({
      month_key: m.month_key,
      items: m.items.map((item) => ({
        kind: item.kind,
        label: item.label,
        projectLabel: item.projectLabel,
        amount: item.amount,
      })),
    })),
  };
}

export type { CashFlowProjectionInputs, JobCostProjectionSlice, ObligationSlice, ForecastBucket };
